var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// x-swipe.ts
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { ComponentSizeController } from "@adornis/chemistry/controllers/component-size-controller.js";
import { event } from "@adornis/chemistry/decorators/event.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-icon.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { animationFrameScheduler, BehaviorSubject, combineLatest, firstValueFrom, Subject } from "rxjs";
import { distinctUntilChanged, filter, map, takeUntil, throttleTime } from "rxjs/operators";
import { Pan, Region, Track } from "westures";
var SWIPE_DISTANCE = 60;
var XSwipe = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.length = 0;
    this.currentSlide = new RXController(this, 0);
    this.controls = false;
    this.hidedots = false;
    this.noswipe = false;
    this.nohide = false;
    this["use-outer-height"] = false;
    this.slides = 1;
    this.distanceX = 0;
    this.distanceY = 0;
    this.size = new ComponentSizeController(this);
    this.initialized = false;
    this.assignedElements = new BehaviorSubject(null);
    /** subject to throttle style updates on pan */
    this.panUpdate = new Subject();
    this._currentKey = new RXController(
      this,
      combineLatest([this.assignedElements, this.currentSlide.observable]).pipe(
        map(
          ([assignedElements, slide]) => (assignedElements || [])[slide]?.getAttribute("key") ?? ""
        )
      )
    );
  }
  styles() {
    return [
      {
        ":host": { position: "relative" },
        "::slotted(*)": { flex: "none", width: `${(1 / (this.length || 1) / this.slides * 100).toFixed(2)}%` },
        "::slotted(.iron-selected)": { opacity: "1 !important" }
      }
    ];
  }
  set currentKey(val) {
    firstValueFrom(this.assignedElements.pipe(filter(Boolean))).then((assignedElements) => {
      const slideIndex = assignedElements.map((assignedElement) => assignedElement.getAttribute("key")).indexOf(val);
      this.currentSlide.next(slideIndex === -1 ? this.currentSlide.value : slideIndex);
    });
  }
  get currentKey() {
    return this._currentKey;
  }
  //#region westures
  trackPhase(data) {
    data.phase === "end" ? this.endPan(data) : this.startPan(data);
  }
  startPan(data) {
    this._slides.style.transition = "none";
    this._slides.style.pointerEvents = "none";
    if (!data.target.westuresData)
      data.target.westuresData = { x: 0, y: 0 };
    data.target.westuresData.y = this.distanceY;
  }
  endPan(data) {
    this._slides.style.transition = "transform .12s ease-out";
    this._slides.style.pointerEvents = "unset";
    if (Math.abs(data.target.westuresData.x) > SWIPE_DISTANCE) {
      this.distanceY = 0;
      if (data.target.westuresData.x < 0)
        this.forward();
      else if (data.target.westuresData.x > 0)
        this.back();
    }
    this.distanceX = 0;
    data.target.westuresData = { x: 0, y: 0 };
  }
  handlePan(data) {
    if (!data.target.westuresData)
      data.target.westuresData = { x: 0, y: 0 };
    data.target.westuresData.x += data.translation.x;
    data.target.westuresData.y += data.translation.y;
    this.panUpdate.next({ x: data.target.westuresData.x, y: data.target.westuresData.y });
  }
  updateGestures() {
    if (!this._slot || !this.westuresRegion)
      return;
    for (const el of this._slot.assignedElements()) {
      if (!this.westuresRegion.getGesturesByElement(el).size) {
        this.westuresRegion.addGesture(new Pan(el, (data) => this.handlePan(data)));
        this.westuresRegion.addGesture(
          new Track(el, (data) => this.trackPhase(data), {
            phases: ["start", "end"]
          })
        );
      }
    }
  }
  removeAllGestures() {
    if (!this._slot || !this.westuresRegion)
      return;
    for (const el of this._slot.assignedElements())
      this.westuresRegion.removeGesturesByElement(el);
  }
  initializeSwipe() {
    requestAnimationFrame(() => {
      if (this.noswipe || !this._slides)
        return;
      if (this.westuresRegion)
        return;
      this.westuresRegion = new Region(this._slides);
      this.panUpdate.pipe(throttleTime(0, animationFrameScheduler), takeUntil(this.disconnected)).subscribe((data) => {
        if (Math.abs(data.x) < Math.abs(data.y)) {
          this.distanceY = Math.min(0, data.y);
          return;
        }
        if (this.currentSlide.value === this.length - this.slides && data.x < 0)
          return;
        if (this.currentSlide.value === 0 && data.x > 0)
          return;
        this.distanceX = data.x;
      });
      this.updateGestures();
      this._slides.addEventListener("touchend", (e) => {
        this._slides.style.transition = "transform .12s ease-out";
        this._slides.style.pointerEvents = "unset";
        if (this.distanceX !== 0)
          this.distanceX = 0;
      });
    });
  }
  //#endregion
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has("currentSlide"))
      this.slideChanged();
    if (changedProperties.has("nohide")) {
      const assignedElements = this.getAssignedElements();
      assignedElements.forEach((node) => node.style.opacity = this.nohide ? "1" : "0");
    }
    if (changedProperties.has("noswipe")) {
      if (this.noswipe) {
        this.removeAllGestures();
        this.westuresRegion = void 0;
      } else
        this.initializeSwipe();
    }
  }
  firstUpdated() {
    if (this.initialized)
      return;
    if (!this._slot)
      return;
    this.initialized = true;
    this.currentSlide.observable.pipe(distinctUntilChanged(), takeUntil(this.disconnected)).subscribe((slide) => this.slideChanged());
    const assignedElements = this.getAssignedElements();
    this.length = assignedElements.length;
    this.assignedElements.next(assignedElements);
    if (assignedElements.every((el) => !el.classList.contains("iron-selected")))
      assignedElements[0]?.classList.add("iron-selected");
    this.initializeSwipe();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeAllGestures();
    this.westuresRegion = void 0;
  }
  render() {
    return xComponents(html`
      <div
        id="swipeWrapper"
        ${css({
      width: this.size.width ? this.size.width + "px" : "",
      overflow: "hidden",
      height: this["use-outer-height"] ? "100%" : "",
      position: "relative"
    })}
      >
        <x-flex
          id="slides"
          ${ref((slides) => this._slides = slides)}
          ${css({
      width: this.size.width ? this.size.width * this.length + "px" : "",
      height: this["use-outer-height"] ? "100%" : "",
      transition: this.distanceX !== 0 ? "none" : "transform .2s",
      transform: this.size.width ? `translate3d(${-this.size.width * this.currentSlide.value / this.slides + this.distanceX}px, ${this.distanceY}px, 0)` : ""
    })}
          horizontal
        >
          <slot
            ${ref((slot) => this._slot = slot)}
            @slotchange=${() => this.updateGestures()}
          ></slot>
        </x-flex>
        ${this.swipeControls(this.controls, this.hidedots, this.currentSlide.value, this.length)}
      </div>
    `);
  }
  swipeControls(controls, hideDots, currentSlide, length) {
    return controls || !hideDots ? html`
          <x-flex
            class="buttons"
            padding="sm"
            horizontal
            center
            crossaxis-center
            ${css({ width: "100%", position: "absolute", bottom: "0" })}
          >
            ${controls ? this.backButton(currentSlide) : ""} ${hideDots ? "" : this.dots(currentSlide)}
            ${controls ? this.forwardButton(currentSlide, length) : ""}
          </x-flex>
        ` : "";
  }
  backButton(currentSlide) {
    return html`
      <x-icon
        id="back"
        ?invisible=${currentSlide === 0}
        @click="${this.back.bind(this)}"
        ${css({ cursor: "pointer", fontSize: "3em", userSelect: "none" })}
      >
        chevron_left
      </x-icon>
    `;
  }
  forwardButton(currentSlide, length) {
    return html`
      <x-icon
        id="forward"
        ?invisible="${length && currentSlide === length - 1}"
        @click="${this.forward.bind(this)}"
        ${css({ cursor: "pointer", fontSize: "3em", userSelect: "none" })}
      >
        chevron_right
      </x-icon>
    `;
  }
  dots(currentSlide) {
    return html`
      <x-flex horizontal space="xs" id="points">
        ${repeat(
      [...Array(this.length)].map((_, i) => i),
      (i) => i,
      (index) => html` <x-text ${css({ color: index === currentSlide ? "black" : "grey" })}> ● </x-text> `
    )}
      </x-flex>
    `;
  }
  slideChanged() {
    this.style.setProperty("--selected", (-this.currentSlide.value).toString());
    this.selectIndex(this.currentSlide.value);
  }
  forward() {
    this.currentSlide.next(
      this.currentSlide.value < this.length - this.slides ? this.currentSlide.value + 1 : this.currentSlide.value
    );
  }
  back() {
    this.currentSlide.next(this.currentSlide.value > 0 ? this.currentSlide.value - 1 : this.currentSlide.value);
  }
  selectIndex(index) {
    const assignedElements = this.getAssignedElements();
    if (!assignedElements.length)
      return;
    this.removeSelected(assignedElements);
    this.addSelected(assignedElements, index);
  }
  removeSelected(nodes) {
    for (const node of nodes) {
      node.classList.remove("iron-selected");
    }
  }
  addSelected(nodes, index) {
    nodes[index]?.classList.add("iron-selected");
  }
  getAssignedElements() {
    if (!this._slot)
      return [];
    return this._slot.assignedElements({ flatten: true });
  }
};
__decorateClass([
  state(),
  event
], XSwipe.prototype, "length", 2);
__decorateClass([
  state(),
  event
], XSwipe.prototype, "currentSlide", 2);
__decorateClass([
  property({ type: Boolean })
], XSwipe.prototype, "controls", 2);
__decorateClass([
  property({ type: Boolean })
], XSwipe.prototype, "hidedots", 2);
__decorateClass([
  property({ type: Boolean })
], XSwipe.prototype, "noswipe", 2);
__decorateClass([
  property({ type: Boolean })
], XSwipe.prototype, "nohide", 2);
__decorateClass([
  property({ type: Boolean })
], XSwipe.prototype, "use-outer-height", 2);
__decorateClass([
  property({ type: Number })
], XSwipe.prototype, "slides", 2);
__decorateClass([
  state()
], XSwipe.prototype, "distanceX", 2);
__decorateClass([
  state()
], XSwipe.prototype, "distanceY", 2);
__decorateClass([
  state()
], XSwipe.prototype, "size", 2);
__decorateClass([
  state(),
  event
], XSwipe.prototype, "currentKey", 1);
XSwipe = __decorateClass([
  customElement("x-swipe")
], XSwipe);
export {
  XSwipe
};
